import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGetLoginUserprivilege } from '../../../../api/auth/auth';
import { GetPsUpdateListParams, PsCheckCheck } from '../../../../api/model';
import { useGetPsCheckCheckList } from '../../../../api/ps-checks/ps-checks';
import { useGetPsChapterList } from '../../../../api/pss/pss';
import { useDefaultOptions } from '../../../../hooks/api';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import ErrorComponent from '../../../atoms/ErrorComponent';
import NoDataComponent from '../../../atoms/NoDataComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import PageNavi from '../../../modules/PageNavi';
import {
  DialogType,
  getCheckMarkType,
  getDialogTypeFromLoginInfo,
  getDialogTypeStr,
  getFormatDateYYYYMMDD,
  getTempState,
} from './PsListCommmon';
import { SortableTh } from './PsListForm';

export interface PsCheckListTabHandler {
  refresh: () => Promise<void>;
  checkNextPs: (index: number) => Promise<{
    psVersionId: number;
    index: number;
  }>;
}
interface Props {
  callModal: (psVersionId: number | undefined, tempState: number, selectedIndex: number) => void;
}

/**
 * 確認申請タブ
 */
const PsCheckListTab = forwardRef<PsCheckListTabHandler, Props>(({ callModal }, ref) => {
  // 1ページに表示する行数:50行
  const N_DATA_PER_PAGE = 50;

  // 翻訳用定数
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  // 検索条件 章
  const defaultCharterId = sessionStorageUtils.get('Pss.Check.ChapterId', '');
  const [selectedCharterId, setSelectedCharterId] = useState(defaultCharterId);
  // 検索条件 作成者
  const defaultAuthor = sessionStorageUtils.get('Pss.Check.TempAuthor', '');
  const [selectedAuthor, setSelectedAuthor] = useState(defaultAuthor);

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  /* PS章一覧取得 */
  const {
    data: psChapterListData,
    isLoading: psChapterListIsLoading,
    isError: psChapterListIsError,
    error: psChapterListError,
  } = useGetPsChapterList(options);
  const chapterPsIdNum =
    selectedCharterId === 'all' || selectedCharterId === '' ? undefined : Number(selectedCharterId);
  const createUserIdNum = selectedAuthor === 'all' || selectedAuthor === '' ? undefined : Number(selectedAuthor);
  const updateListParams: GetPsUpdateListParams = { chapter_ps_id: chapterPsIdNum, create_user_id: createUserIdNum };

  // ログインユーザ情報取得
  const {
    data: loginUserData,
    isLoading: loginUserIsLoading,
    isError: loginUserIsError,
    error: loginUserError,
  } = useGetLoginUserprivilege(options);

  /* PS確認申請一覧取得 */
  const {
    data: psCheckCheckData,
    refetch: psCheckCheckRefetch,
    isLoading: psCheckCheckIsLoading,
    isError: psCheckCheckIsError,
    error: psCheckCheckError,
  } = useGetPsCheckCheckList(updateListParams, options);
  const uniqueUsers = useRef<PsCheckCheck[]>();

  /** ページネーション */
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('Pss.Check.CurrentPage', 1));

  /** ソート */
  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('Pss.Check.SortInfo', { key: ['updated_at'], isDisc: true })
  );

  /** ソート処理用の関数 */
  const sortPsCheckChecks = useCallback(
    (items: PsCheckCheck[]) =>
      [...items].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;

        // 配列にソートキーがセットされていない場合は何もしない
        if (sortInfo.key.length === 0) {
          return 0;
        }

        // 配列にソートキーが1つだけの場合
        if (sortInfo.key.length === 1) {
          return ((aAny[sortInfo.key[0]] ?? '') > (bAny[sortInfo.key[0]] ?? '') ? 1 : -1) * multiValue;
        }

        // 配列にソートキーが2つ以上の場合(2つとみなして並び替え)
        // （所属 + 作成者)はソートキーが2つある
        if ((aAny[sortInfo.key[0]] ?? '') !== (bAny[sortInfo.key[0]] ?? '')) {
          return ((aAny[sortInfo.key[0]] ?? '') > (bAny[sortInfo.key[0]] ?? '') ? 1 : -1) * multiValue;
        }
        return ((aAny[sortInfo.key[1]] ?? '') > (bAny[sortInfo.key[1]] ?? '') ? 1 : -1) * multiValue;
      }),
    [sortInfo]
  );

  /** ソート処理 */
  const allPss = useMemo(
    () => sortPsCheckChecks(psCheckCheckData?.data.ps_check_checks ?? []),
    [psCheckCheckData, sortPsCheckChecks]
  );

  /** ページネーションで一覧をフィルタリングする */
  const filteredPss = useMemo(
    () => allPss.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, allPss]
  );

  /** ソートキーを保存 */
  const changeSortKey = useCallback((key: string[]) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: JSON.stringify(pre.key) === JSON.stringify(key) && !pre.isDisc };
      sessionStorageUtils.set('Pss.Check.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  /** ページネーションを保存 */
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('Pss.Check.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  useEffect(() => {
    // ページネーションを1に戻す
    if (psCheckCheckData?.data.ps_check_checks != null) {
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        // setCurrentPage(1);
      }
    }
  }, [psCheckCheckData?.data.ps_check_checks]);

  // 次PSバージョンID取得
  const checkNextPs = useCallback(
    async (index: number) => {
      // 最新のデータを取得する
      const { data } = await psCheckCheckRefetch();
      const psCheckChecks = sortPsCheckChecks(data?.data.ps_check_checks ?? []);
      // リストが空の場合は -1 を返す
      if (psCheckChecks == null || psCheckChecks.length === 0) {
        return { psVersionId: -1, index: -1 };
      }
      // インデックスがリストの範囲外の場合は、インデックスをリストの最後のインデックスにする(インデックスが0の場合はそのまま0)
      let idx = index;
      let cpg = currentPage;
      if (idx > 0 && psCheckChecks.length < index + (cpg - 1) * N_DATA_PER_PAGE + 1) {
        idx = psCheckChecks.length - (cpg - 1) * N_DATA_PER_PAGE - 1;
      }
      // 2ページ目以降でインデックスが0かつ後ろにデータがない場合は、ページを1つマイナスして、インデックスを最後にする
      if (cpg > 1 && idx === 0 && psCheckChecks.length <= (cpg - 1) * N_DATA_PER_PAGE + idx) {
        cpg -= 1;
        setCurrentPage(cpg);
        idx = N_DATA_PER_PAGE - 1;
      }
      // インデックスを計算する(ページネーションを考慮)
      let listIndex = idx + (cpg - 1) * N_DATA_PER_PAGE;
      // インデックスがリストの範囲外の場合は、リストの最後のインデックスを返す
      if (psCheckChecks.length <= listIndex) {
        listIndex = psCheckChecks.length - 1;
      }
      // リストから次のPSのバージョンIDを返す
      return {
        psVersionId: psCheckChecks[listIndex].ps_version_id ?? -1,
        index: idx,
      };
    },
    [currentPage, psCheckCheckRefetch, sortPsCheckChecks]
  );

  // 親コンポーネントからの操作
  useImperativeHandle(ref, () => ({
    refresh: async () => {
      await psCheckCheckRefetch();
    },
    checkNextPs,
  }));

  /* 章一覧のデータ */
  // ローディング
  if (psChapterListIsLoading) {
    return <WaitComponent />;
  }

  // エラー時
  if (psChapterListIsError) {
    return <ErrorComponent error={psChapterListError} />;
  }

  // ログインユーザ情報
  if (loginUserIsLoading) {
    return <WaitComponent />;
  }
  if (loginUserIsError) {
    return <ErrorComponent error={loginUserError} />;
  }

  /* PS確認申請タブのデータ */
  // ローディング
  if (psCheckCheckIsLoading) {
    return <WaitComponent />;
  }

  // エラー時
  if (psCheckCheckIsError) {
    return <ErrorComponent error={psCheckCheckError} />;
  }

  // データがなかった場合
  if (
    psChapterListData == null ||
    psChapterListData.data.ps_chapters == null ||
    loginUserData == null ||
    loginUserData.data == null ||
    psCheckCheckData == null ||
    psCheckCheckData.data == null ||
    psCheckCheckData.data.ps_check_checks == null ||
    psCheckCheckData.data.ps_check_checks === undefined
  ) {
    return <NoDataComponent />;
  }

  /* PS作成者一覧取得 */
  if (
    (selectedCharterId === 'all' ||
      selectedCharterId === undefined ||
      selectedCharterId === null ||
      selectedCharterId === '') &&
    (selectedAuthor === 'all' || selectedAuthor === undefined || selectedAuthor === null || selectedAuthor === '')
  ) {
    uniqueUsers.current = Array.from(
      new Map(psCheckCheckData.data.ps_check_checks.map((user) => [user.create_user_id, user])).values()
    );
    sessionStorageUtils.set('Pss.Check.CreateUser', uniqueUsers.current);
  } else {
    uniqueUsers.current = sessionStorageUtils.get('Pss.Check.CreateUser', []);
  }

  return (
    <div>
      {/* 検索条件 */}
      <div className="ps-search">
        <div>
          <label htmlFor="chapter">{t('章の絞り込み')}</label>
          <span className="dropdown chapter">
            <select
              onChange={(e) => {
                setSelectedCharterId(e.target.value);
                sessionStorageUtils.set('Pss.Check.ChapterId', e.target.value);
              }}
              value={selectedCharterId}
            >
              <option value="all" key="all">
                {t('すべて')}
              </option>
              {psChapterListData.data.ps_chapters.map((x) => (
                <option value={x.chapter_ps_id} key={x.chapter_ps_id}>
                  {x.chapter_ps_no}
                </option>
              ))}
            </select>
          </span>
        </div>
        <div>
          <label htmlFor="maker">{t('作成者の絞り込み')}</label>
          <span className="dropdown maker">
            <select
              onChange={(e) => {
                setSelectedAuthor(e.target.value);
                sessionStorageUtils.set('Pss.Check.TempAuthor', e.target.value);
              }}
              value={selectedAuthor}
            >
              <option value="all" key="all">
                {t('すべて')}
              </option>
              {uniqueUsers.current !== undefined
                ? uniqueUsers.current.map((x) => (
                    <option value={x.create_user_id} key={x.create_user_id}>
                      {x.create_user_membership_name} {x.create_user}
                    </option>
                  ))
                : ''}
            </select>
          </span>
        </div>
      </div>
      <table className="table28">
        <tbody>
          {/* ラベル クリックで並び替え */}
          <tr>
            <SortableTh label={t('管理No.')} onClick={() => changeSortKey(['ps_version_id'])} />
            <SortableTh label={t('タイトル')} onClick={() => changeSortKey(['title'])} />
            <SortableTh
              label={t('作成者')}
              onClick={() => changeSortKey(['create_user_membership_name', 'create_user'])}
            />
            <SortableTh label={t('状態')} onClick={() => changeSortKey(['temp_state'])} />
            <SortableTh label={t('申請日')} onClick={() => changeSortKey(['updated_at'])} />
            <SortableTh label={t('操作')} onClick={() => changeSortKey(['temp_state'])} />
            <th>{}</th>
            <th>{}</th>
          </tr>
          {/* データ表示部 */}
          {filteredPss.map((data, index) => (
            <tr key={data.ps_no?.toString()}>
              <td>
                <Link to={`/${language}/ps/view/${data.ps_version_id?.toString() ?? ''}`}> {data.ps_no}</Link>
              </td>
              <td>{data.title}</td>
              <td>
                {data.create_user_membership_name} {data.create_user}
              </td>
              <td>{getTempState(data.temp_state ?? -1, t)}</td>
              <td>{getFormatDateYYYYMMDD(data.created_at)}</td>
              <td>
                <button
                  type="button"
                  className="text-button"
                  onClick={(e) => {
                    e.preventDefault();
                    callModal(data.ps_version_id, DialogType.STATE_HISTORY, index);
                  }}
                >
                  <span className="modal-open">
                    <i className="fa-solid fa-list" />
                    {getDialogTypeStr(DialogType.STATE_HISTORY, t)}
                  </span>
                </button>
              </td>
              <td>
                <button
                  type="button"
                  className="text-button"
                  onClick={(e) => {
                    e.preventDefault();
                    callModal(
                      data.ps_version_id,
                      getDialogTypeFromLoginInfo(data.temp_state, loginUserData.data),
                      index
                    );
                  }}
                >
                  <span className="modal-open text-button">
                    <i className={`fa-solid ${getCheckMarkType(data.temp_state, loginUserData.data)}`} />
                    {getDialogTypeStr(getDialogTypeFromLoginInfo(data.temp_state, loginUserData.data), t)}
                  </span>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* ページング */}
      <PageNavi
        current={currentPage}
        pageCount={Math.ceil(psCheckCheckData.data.ps_check_checks.length / (N_DATA_PER_PAGE * 1.0))}
        onChangePage={changeCurrentPage}
      />
    </div>
  );
});

export default PsCheckListTab;
